/**
 * Board tree store: the active organization's folders and boards, the sidebar's data and
 * every folder and board mutation. Loaded per organization by session_scope and reset
 * when the organization changes. Reads swallow failures and leave isLoaded false so the
 * next trigger retries; writes return an error message or null. Not persisted.
 */
import type {IBoard} from '@models/board';
import type {IBoardFolder} from '@models/board_folder';
import {trpcClient} from '@shared_ui/trpc_client';
import {buildTrpcErrorMessage} from '@shared_ui/trpc_errors';
import type {IRankPlacement} from '@shared/board_types';
import {useBoardStore} from '@src/store/board_store';
import {
   buildTreeRows,
   type ITreeRow,
   TreeRowKind,
   upsertRankedById,
} from '@src/store/board_tree_helpers';
import {useOrgStore} from '@src/store/org_store';
import {defineStore} from 'pinia';

export interface IBoardTreeState {
   folders: IBoardFolder[];
   boards: IBoard[];
   /** Folders the user has expanded; session-only. */
   openFolderIds: string[];
   isLoaded: boolean;
   /** A read is in flight; a second trigger for the same organization waits for it. */
   isLoading: boolean;
   /** Organization the loaded tree belongs to; guards organization switches. */
   loadedForOrgId: string | null;
}

/** Folder fields a save may change; an absent key leaves the stored value alone. */
export type FolderPatch = Partial<Pick<IBoardFolder, 'name'>> & {color?: string};

/** Board fields a save may change; an absent key leaves the stored value alone. */
export type BoardPatch = Partial<Pick<IBoard, 'name' | 'description'>> & {color?: string};

const NO_ORGANIZATION_MESSAGE = 'No active organization';

export const useBoardTreeStore = defineStore('boardTree', {
   state: (): IBoardTreeState => ({
      folders: [],
      boards: [],
      openFolderIds: [],
      isLoaded: false,
      isLoading: false,
      loadedForOrgId: null,
   }),

   getters: {
      /** Sidebar rows for the tree, narrowed by the filter; empty until loaded. */
      treeRows(state): (filter_text: string, active_board_id: string | null) => ITreeRow[] {
         return (filter_text, active_board_id) =>
            buildTreeRows({
               folders: state.folders,
               boards: state.boards,
               openFolderIds: state.openFolderIds,
               activeBoardId: active_board_id,
               filterText: filter_text,
            });
      },

      /** Every board in tree order, folders expanded, for the collapsed rail. */
      boardsInTreeOrder(state): IBoard[] {
         const rows = buildTreeRows({
            folders: state.folders,
            boards: state.boards,
            openFolderIds: state.folders.map((folder) => folder.id),
            activeBoardId: null,
            filterText: '',
         });
         return rows
            .filter((row) => row.kind === TreeRowKind.BOARD)
            .map((row) => state.boards.find((board) => board.id === row.id))
            .filter((board): board is IBoard => board != null);
      },

      /** Folders a folder may be moved into: all except itself and its descendants, sorted by name. */
      folderMoveTargets(state): (folder_id: string | null) => IBoardFolder[] {
         return (folder_id) => {
            const excluded = new Set<string>();
            if (folder_id != null) {
               excluded.add(folder_id);
               let grew = true;
               while (grew) {
                  grew = false;
                  for (const folder of state.folders) {
                     if (
                        folder.parentFolderId != null &&
                        excluded.has(folder.parentFolderId) &&
                        !excluded.has(folder.id)
                     ) {
                        excluded.add(folder.id);
                        grew = true;
                     }
                  }
               }
            }
            return state.folders
               .filter((folder) => !excluded.has(folder.id))
               .sort((a, b) => a.name.localeCompare(b.name));
         };
      },
   },

   actions: {
      // ---- [ Lookups ] ---- //

      /** Find a loaded board by id; null when the id is null or no board matches. */
      boardById(board_id: string | null | undefined): IBoard | null {
         if (board_id == null) {
            return null;
         }
         return this.boards.find((board) => board.id === board_id) ?? null;
      },

      /** Find a loaded folder by id; null when the id is null or no folder matches. */
      folderById(folder_id: string | null | undefined): IBoardFolder | null {
         if (folder_id == null) {
            return null;
         }
         return this.folders.find((folder) => folder.id === folder_id) ?? null;
      },

      // ---- [ Loading ] ---- //

      /**
       * Load the tree for the active organization unless it is already loaded for it.
       * Resets the store when there is no active organization. Cheap to call repeatedly.
       */
      async ensureLoadedForOrg(): Promise<void> {
         const organization_id = useOrgStore().activeOrg?.id ?? null;
         if (organization_id == null) {
            this.$reset();
            return;
         }
         if ((this.isLoaded || this.isLoading) && this.loadedForOrgId === organization_id) {
            return;
         }

         this.$reset();
         this.loadedForOrgId = organization_id;
         await this.reload();
      },

      /**
       * Re-read the folders and boards; clears both and leaves isLoaded false on failure.
       * A response for an organization that is no longer the loaded one is discarded.
       */
      async reload(): Promise<void> {
         const organization_id = this.loadedForOrgId;
         if (organization_id == null) {
            return;
         }

         this.isLoading = true;
         try {
            const result = await trpcClient().boards.list.query({organizationId: organization_id});
            if (this.loadedForOrgId !== organization_id) {
               return;
            }
            this.folders = result.folders;
            this.boards = result.boards;
            this.isLoaded = true;
         } catch (err) {
            if (this.loadedForOrgId !== organization_id) {
               return;
            }
            console.warn('Could not load the board tree', err);
            this.folders = [];
            this.boards = [];
            this.isLoaded = false;
         } finally {
            if (this.loadedForOrgId === organization_id) {
               this.isLoading = false;
            }
         }
      },

      // ---- [ Folders ] ---- //

      /** Create a folder; returns the new folder or an error message. */
      async createFolder(opts: {
         name: string;
         color?: string;
         parentFolderId: string | null;
      }): Promise<{folder: IBoardFolder} | {error: string}> {
         const organization_id = this.loadedForOrgId;
         if (organization_id == null) {
            return {error: NO_ORGANIZATION_MESSAGE};
         }

         try {
            const folder = await trpcClient().boardFolders.create.mutate({
               organizationId: organization_id,
               name: opts.name,
               color: opts.color,
               parentFolderId: opts.parentFolderId ?? undefined,
            });
            this.applyFolder(folder);
            if (opts.parentFolderId != null) {
               this.openFolder(opts.parentFolderId);
            }
            return {folder};
         } catch (err) {
            return {error: buildTrpcErrorMessage(err, 'Could not create the folder')};
         }
      },

      /** Update the details of a folder. */
      async updateFolder(folder_id: string, patch: FolderPatch): Promise<string | null> {
         const organization_id = this.loadedForOrgId;
         if (organization_id == null) {
            return NO_ORGANIZATION_MESSAGE;
         }

         try {
            const folder = await trpcClient().boardFolders.update.mutate({
               organizationId: organization_id,
               folderId: folder_id,
               ...patch,
            });
            this.applyFolder(folder);
            return null;
         } catch (err) {
            return buildTrpcErrorMessage(err, 'Could not update the folder');
         }
      },

      /** Move a folder under a parent (null = top level) at the placement; null placement appends. */
      async moveFolder(opts: {
         folderId: string;
         parentFolderId: string | null;
         placement: IRankPlacement | null;
      }): Promise<string | null> {
         const organization_id = this.loadedForOrgId;
         if (organization_id == null) {
            return NO_ORGANIZATION_MESSAGE;
         }

         try {
            const folder = await trpcClient().boardFolders.move.mutate({
               organizationId: organization_id,
               folderId: opts.folderId,
               parentFolderId: opts.parentFolderId,
               placement: opts.placement ?? undefined,
            });
            this.applyFolder(folder);
            return null;
         } catch (err) {
            await this.reload();
            return buildTrpcErrorMessage(err, 'Could not move the folder');
         }
      },

      /** Delete a folder; its subfolders and boards move up to its parent. */
      async deleteFolder(folder_id: string): Promise<string | null> {
         const organization_id = this.loadedForOrgId;
         if (organization_id == null) {
            return NO_ORGANIZATION_MESSAGE;
         }

         try {
            const result = await trpcClient().boardFolders.delete.mutate({
               organizationId: organization_id,
               folderId: folder_id,
            });
            this.dropFolder(result.folderId);
            for (const folder of result.folders) {
               this.applyFolder(folder);
            }
            for (const board of result.boards) {
               this.applyBoardSummaryUpdate(board);
            }
            return null;
         } catch (err) {
            return buildTrpcErrorMessage(err, 'Could not delete the folder');
         }
      },

      // ---- [ Boards ] ---- //

      /** Create a board in a folder (null = top level); returns the board or an error message. */
      async createBoard(opts: {
         name: string;
         description?: string | null;
         color?: string;
         folderId: string | null;
      }): Promise<{board: IBoard} | {error: string}> {
         const organization_id = this.loadedForOrgId;
         if (organization_id == null) {
            return {error: NO_ORGANIZATION_MESSAGE};
         }

         try {
            const board = await trpcClient().boards.create.mutate({
               organizationId: organization_id,
               name: opts.name,
               description: opts.description,
               color: opts.color,
               folderId: opts.folderId ?? undefined,
            });
            this.applyBoardSummaryUpdate(board);
            if (opts.folderId != null) {
               this.openFolder(opts.folderId);
            }
            return {board};
         } catch (err) {
            return {error: buildTrpcErrorMessage(err, 'Could not create the board')};
         }
      },

      async updateBoard(board_id: string, patch: BoardPatch): Promise<string | null> {
         const organization_id = this.loadedForOrgId;
         if (organization_id == null) {
            return NO_ORGANIZATION_MESSAGE;
         }

         try {
            const board = await trpcClient().boards.update.mutate({
               organizationId: organization_id,
               boardId: board_id,
               ...patch,
            });
            this.applyBoardSummaryUpdate(board);
            useBoardStore().applyBoardDetail(board);
            return null;
         } catch (err) {
            return buildTrpcErrorMessage(err, 'Could not update the board');
         }
      },

      /** Move a board into a folder (null = top level) at the placement; null placement appends. */
      async moveBoard(opts: {
         boardId: string;
         folderId: string | null;
         placement: IRankPlacement | null;
      }): Promise<string | null> {
         const organization_id = this.loadedForOrgId;
         if (organization_id == null) {
            return NO_ORGANIZATION_MESSAGE;
         }

         try {
            const board = await trpcClient().boards.move.mutate({
               organizationId: organization_id,
               boardId: opts.boardId,
               folderId: opts.folderId,
               placement: opts.placement ?? undefined,
            });
            this.applyBoardSummaryUpdate(board);
            useBoardStore().applyBoardDetail(board);
            return null;
         } catch (err) {
            await this.reload();
            return buildTrpcErrorMessage(err, 'Could not move the board');
         }
      },

      /** Delete a board and everything on it. */
      async deleteBoard(board_id: string): Promise<string | null> {
         const organization_id = this.loadedForOrgId;
         if (organization_id == null) {
            return NO_ORGANIZATION_MESSAGE;
         }

         try {
            const result = await trpcClient().boards.delete.mutate({
               organizationId: organization_id,
               boardId: board_id,
            });
            this.dropBoard(result.boardId);
            if (useBoardStore().loadedBoardId === result.boardId) {
               useBoardStore().clear();
            }
            return null;
         } catch (err) {
            return buildTrpcErrorMessage(err, 'Could not delete the board');
         }
      },

      // ---- [ Tree UI state ] ---- //

      toggleFolder(folder_id: string): void {
         if (this.openFolderIds.includes(folder_id)) {
            this.openFolderIds = this.openFolderIds.filter((id) => id !== folder_id);
         } else {
            this.openFolderIds.push(folder_id);
         }
      },

      openFolder(folder_id: string): void {
         if (!this.openFolderIds.includes(folder_id)) {
            this.openFolderIds.push(folder_id);
         }
      },

      // ---- [ Row entry points ] ---- //

      /** Insert or replace a folder row by id. */
      applyFolder(folder: IBoardFolder): void {
         this.folders = upsertRankedById(this.folders, folder);
      },

      dropFolder(folder_id: string): void {
         this.folders = this.folders.filter((folder) => folder.id !== folder_id);
      },

      /**
       * Insert or replace a board's summary row by id. The board store calls this after it
       * changes the open board so the sidebar stays in step. Takes the full board today;
       * narrows to a board summary once the list endpoint returns one.
       */
      applyBoardSummaryUpdate(board: IBoard): void {
         this.boards = upsertRankedById(this.boards, board);
      },

      dropBoard(board_id: string): void {
         this.boards = this.boards.filter((board) => board.id !== board_id);
      },
   },
});
